import React from "react";
import PropTypes from "prop-types";
import { graphql, useStaticQuery } from "gatsby";

import { classNames } from "@/helpers/classNames";

import MediaWrapper from "@/components/mediaWrapper";
import LinkWrapper from "@/components/linkWrapper";
import MarkdownWrapper from "@/components/markdownWrapper";

export const query = graphql`
  fragment FooterFourColumnWithAddressArticle on StrapiArticle {
    locale
    date
    url
    title
  }

  fragment FooterFourColumnWithAddressProcessedProduct on ShopifyProcessedProduct {
    title
    url
    productType
    productTypeUrl
  }

  fragment FooterFourColumnWithAddressData on StrapiComponentFooterFourColumnWithAddress {
    addressTitle
    address
    socialLinks {
      ...StrapiSharedLink
    }
    copyrightText

    column1Title
    column1Links {
      ...StrapiSharedLink
    }
    column2Title
    column2Links {
      ...StrapiSharedLink
    }
    column3Title
    column3Links {
      ...StrapiSharedLink
    }
    blogTitle
    blogArticleCount
    certificate {
      ...StrapiSharedLink
    }
  }
`;

function createLinkColumn(title, links) {
  if (!title) {
    return <></>;
  }
  return (
    <div>
      <h3 className="text-sm font-semibold uppercase tracking-wider text-text-gray">
        <MarkdownWrapper text={title} />
      </h3>
      <ul role="list" className="mt-4 space-y-4">
        {Boolean(links?.length) &&
          links.map(
            (item, index) =>
              (item.type ||
                item.title ||
                item.overwriteTitle ||
                item.link?.title) && (
                <li key={index}>
                  <LinkWrapper
                    to={item.url || item.link?.url}
                    className="text-base text-text-secondary hover:text-text-gray"
                  >
                    <MarkdownWrapper
                      text={
                        item.type ||
                        item.title ||
                        item.overwriteTitle ||
                        item.link?.title
                      }
                    />
                  </LinkWrapper>
                </li>
              )
          )}
      </ul>
    </div>
  );
}

function FourColumnWithAddress({ data, locale, className }) {
  if (!data || !locale) {
    return <></>;
  }

  const query = useStaticQuery(graphql`
    query fourColumnWithAddress {
      articles: allStrapiArticle(sort: { date: DESC }) {
        nodes {
          ...FooterFourColumnWithAddressArticle
        }
      }
    }
  `);
  const articles =
    query?.articles?.nodes
      ?.filter((article) => article.locale === locale)
      ?.slice(
        0,
        data.blogArticleCount && data.blogArticleCount > 0
          ? data.blogArticleCount
          : 3
      ) || [];

  return (
    <footer
      className={classNames("bg-background-white", className)}
      aria-labelledby="footer-heading"
    >
      <h2 id="footer-heading" className="sr-only">
        Footer
      </h2>
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        <div className="xl:grid xl:grid-cols-5 xl:gap-8">
          <div className="sm:col-span-2 sm:grid sm:grid-cols-2 sm:gap-8 xl:col-span-4">
            <div className="md:grid md:grid-cols-2 md:gap-8">
              <div>
                {data.column1Title &&
                  createLinkColumn(data.column1Title, data.column1Links || [])}
              </div>
              <div className="mt-12 md:mt-0">
                {data.column2Title &&
                  createLinkColumn(data.column2Title, data.column2Links || [])}
              </div>
            </div>
            <div className="md:grid md:grid-cols-2 md:gap-8">
              <div className="mt-12 sm:mt-0">
                {data.column3Title &&
                  createLinkColumn(data.column3Title, data.column3Links || [])}
              </div>
              <div className="mt-12 md:mt-0">
                {data.blogTitle &&
                  createLinkColumn(data.blogTitle, articles || [])}
              </div>
            </div>
          </div>
          <div className="mt-12 xl:mt-0">
            {data.addressTitle && data.address && (
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wider text-text-gray">
                  <MarkdownWrapper text={data.addressTitle} />
                </h3>
                <MarkdownWrapper
                  text={data.address}
                  markdownClassName="text-text-secondary mt-4 text-base"
                  replaceParagraph={false}
                  usePlausible
                />
              </div>
            )}
            {(data.certificate?.overwriteIcon ||
              data.certificate?.link?.icon) && (
              <LinkWrapper to={data.certificate?.link?.url}>
                <MediaWrapper
                  media={
                    data.certificate?.overwriteIcon ||
                    data.certificate?.link?.icon
                  }
                  objectFit="contain"
                  divClassName="mt-4"
                  aria-hidden="true"
                />
              </LinkWrapper>
            )}
          </div>
        </div>
        <div className="mt-8 border-t border-border-light-gray pt-8 md:flex md:items-center md:justify-between">
          {data.socialLinks && (
            <div className="mb-8 flex space-x-6 md:order-2 md:mb-0">
              {data.socialLinks.map(
                (item, index) =>
                  item.link?.url &&
                  (item.overwriteIcon || item.link?.icon) && (
                    <LinkWrapper
                      key={index}
                      to={item.link.url}
                      className="text-text-secondary hover:text-text-secondary-light"
                    >
                      {(item.overwriteIcon?.alternativeText ||
                        item.link?.icon?.alternativeText) && (
                        <span className="sr-only">
                          {item.overwriteIcon?.alternativeText ||
                            item.link.icon.alternativeText}
                        </span>
                      )}
                      <MediaWrapper
                        media={item.overwriteIcon || item.link.icon}
                        objectFit="contain"
                        divClassName="h-6 w-6"
                        aria-hidden="true"
                      />
                    </LinkWrapper>
                  )
              )}
            </div>
          )}
          <div className="mt-4 flex justify-center md:order-1">
            <span className="inline-block break-words text-text-secondary">
              {data.copyrightText
                ? `© ${new Date().getFullYear()} ${data.copyrightText} `
                : ""}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}

FourColumnWithAddress.propTypes = {
  data: PropTypes.object,
  locale: PropTypes.string,
  className: PropTypes.string,
};

export default FourColumnWithAddress;
